import * as React from 'react';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { LocalesContext } from './locales';
import type { CasinoGamesProps } from './types';
import useGames from './Grid/useGames';
import useGameStarter from './useGameStarter';
import Grid from './Grid';
import PlayPage from './PlayPage';

const CasinoGames: FC<CasinoGamesProps> = (props) => {
  // Only the initial state comes from the request - the same on the server and in the browser.
  const initialLocation = new URL(props.requestPath, 'http://localhost');
  const gameStarter = useGameStarter(props);
  const { state, paginatedGames, resultsVersion, searchParams, pagePath, homepage } = useGames(
    props.paginatedGames,
    gameStarter,
    initialLocation
  );

  const gameScreenRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const display = gameStarter.opened ? 'none' : '';
    // The filters and the banner above the grid (layouts/application); the banner keeps its state
    // (folded or not) for when the game is closed.
    document.querySelectorAll<HTMLElement>('[data-hidden-while-playing]').forEach((el) => {
      el.style.display = display;
    });
    // Only once the filters above are hidden: they would shift the game up under the sticky header.
    // To the game's top border, just under the header (the root's scroll-padding-top plus the
    // screen's scroll-margin-top), not to the page top: the header and whatever sits above the game
    // are of no use to a player.
    if (gameStarter.opened && gameStarter.scrollIntoView) gameScreenRef.current?.scrollIntoView();
  }, [gameStarter.opened]);

  return (
    <LocalesContext.Provider value={props.locales}>
      {gameStarter.opened && (
        <PlayPage
          key={`${gameStarter.game?.id}-${gameStarter.playMode}`}
          gameStarter={gameStarter}
          screenRef={gameScreenRef}
          playerPresent={props.common.playerPresent}
          cashbackEnabled={props.common.cashbackEnabled}
        />
      )}
      {/* A game page (props.game) has no grid to show - it can't be revealed there, see
          BrandXo::CasinoGamesController#skip_games_grid - so it isn't rendered at all, keeping an
          empty "no games found" grid out of the page crawlers read. A game opened from the grid
          only hides it, so closing the game returns to the same loaded list. */}
      {!props.game && (
        <div style={{ display: gameStarter.opened ? 'none' : 'block' }} data-testid="games grid container">
          {/* Hidden along with the grid while a game opened from it is shown, so the page keeps one
              h1 - the game's. Visually hidden: the design has no visible place for it. */}
          {props.heading && <h1 className="xo-visually-hidden">{props.heading}</h1>}
          <Grid
            paginatedGames={paginatedGames}
            resultsVersion={resultsVersion}
            searchParams={searchParams}
            pagePath={pagePath}
            state={state}
            gameStarter={gameStarter}
            playerPresent={props.common.playerPresent}
            homepage={homepage}
          />
        </div>
      )}
    </LocalesContext.Provider>
  );
};

export default CasinoGames;
